import { Box } from "@mui/material";
import Head from "next/head";
import { useTranslation } from "react-i18next";
import { SITE_TYPE, SITE_WRAP } from "src/domains/shared/constants/styles/site";
import { replayOnEnterRef } from "../siteKit/motion/reveal";
import { riseOrder, SITE_HERO_RISE_STYLES } from "../siteKit/siteHeroStyles";
import {
  NEWS_ARTICLES,
  formatPublishedDate,
  type NewsArticle,
} from "./newsArticles";
import { NewsListItem } from "./NewsListItem";
import { NewsRelatedLinks } from "./NewsRelatedLinks";
import { NEWS_LIST_STYLES } from "./newsStyles";
import { SiteTitle } from "../siteKit/SiteText";

function sortByPublishedAtDesc(articles: readonly NewsArticle[]) {
  return [...articles].sort(
    (a, b) =>
      new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  );
}

/** 언론보도 목록(`/news`, press-news): 머리말과 기사 수, 최신순 대표 기사형 행, 맨 아래 「함께 보면 좋아요」. */
export function NewsPage() {
  const { t, i18n } = useTranslation("landing");
  const articles = sortByPublishedAtDesc(NEWS_ARTICLES);
  // 사진은 언론사 CDN 에서 바로 받는다 — 화면 밖 기사 사진도 연결만은 미리 맺어 둔다
  const imageOrigins = [
    ...new Set(articles.map((article) => new URL(article.thumbnail).origin)),
  ];
  return (
    <>
      <Head>
        {imageOrigins.map((origin) => (
          <link key={`preconnect-${origin}`} rel="preconnect" href={origin} />
        ))}
      </Head>
      <Box
        component="section"
        ref={replayOnEnterRef}
        sx={{
          ...SITE_WRAP,
          ...NEWS_LIST_STYLES.head,
          ...SITE_HERO_RISE_STYLES,
        }}
      >
        <div>
          <Box component="p" sx={SITE_TYPE.eyebrow}>
            {t("site.news.eyebrow")}
          </Box>
          <Box component="h1" sx={SITE_TYPE.h1}>
            <SiteTitle text={t("site.news.title")} />
          </Box>
        </div>
        <Box component="p" style={riseOrder(1)} sx={NEWS_LIST_STYLES.count}>
          {t("site.news.count", { n: articles.length })}
        </Box>
      </Box>
      <Box
        component="section"
        aria-label={t("site.news.listLabel")}
        sx={{ ...SITE_WRAP, ...NEWS_LIST_STYLES.list }}
      >
        {articles.length === 0 ? (
          <Box component="p" sx={NEWS_LIST_STYLES.empty}>
            {t("site.news.empty")}
          </Box>
        ) : (
          articles.map((article, index) => (
            <NewsListItem
              key={article.id}
              article={article}
              formattedDate={formatPublishedDate(
                article.publishedAt,
                i18n.language
              )}
              priority={index === 0}
            />
          ))
        )}
      </Box>
      <NewsRelatedLinks />
    </>
  );
}
